import { SearchParameters } from 'algoliasearch-helper'
import { Axios } from 'cms-api'
import dayjs from 'dayjs'
import { type GetStaticProps, type NextPageWithLayout } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import { InstantSearch } from 'react-instantsearch'
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'
import { getPublicLayout } from '@public/components/PublicLayout'
import {
  EXCLUDE_CUSTOM_SALES_FILTER,
  ShopPageLayout,
} from 'components/InstantSearch'
import { BRAND_PAGE_SORT_OPTIONS } from 'components/InstantSearch/TypeSenseSortBy'
import { Seo } from 'components/Seo'
import { prepareGetStaticNavigationProps } from 'routes/utils/prepareGetStaticNavigationProps'
import { getTypesenseConfigurationOptions } from 'utils/typesenseClient'

const { searchClient } = new TypesenseInstantSearchAdapter({
  server: getTypesenseConfigurationOptions(),
  additionalSearchParameters: {
    query_by: 'title,embedding',
    include_fields:
      'id,title,imageUrl,imageAlt,minPrice,maxPrice,minTradePrice,maxTradePrice,minTradeDiscount,legacyProductId,breakoutOptionCount,carouselVariants,legacyProductVariantId,brand,availabilityOrder, itemTypes',
    sort_by: BRAND_PAGE_SORT_OPTIONS[1].value.replace('products-v5/sort/', ''),
  },
})

interface NewArrivalsPageProps {
  numberOfDays: number
  includedBrands: string[]
  includedProductCategories: string[]
}

export const NewArrivalsPage: NextPageWithLayout<NewArrivalsPageProps> = ({
  numberOfDays,
  includedBrands,
  includedProductCategories,
}) => {
  const router = useRouter()
  const [routingKey, setRoutingKey] = useState(0)
  const firstPublishedAt = dayjs().subtract(numberOfDays, 'days').unix()

  // next link has problem with routing when navigate to the same page with different query params
  useEffect(() => {
    setRoutingKey((k) => k + 1)
  }, [router.asPath])

  const categoryByLevel = includedProductCategories.reduce((acc, category) => {
    const levels = category.split(' > ').length - 1
    if (!acc.has(levels)) {
      acc.set(levels, [])
    }
    acc.get(levels)?.push(category)
    return acc
  }, new Map<number, string[]>())

  const categoriesFilters = [...categoryByLevel].map(
    ([level, categories]) =>
      `category.lvl${level}:=[${categories.map((category) => `\`${category}\``).join(',')}]`
  )

  const filters = [
    `firstPublishedAt :> ${firstPublishedAt}`,
    'status:=PUBLISHED',
    EXCLUDE_CUSTOM_SALES_FILTER,
    ...(includedBrands.length > 0
      ? [`brand:=[${includedBrands.map((brand) => `\`${brand}\``).join(',')}]`]
      : []),
    ...(categoriesFilters.length > 0
      ? [`(${categoriesFilters.join(' || ')})`]
      : []),
  ].join(' && ')

  const searchParameters = useMemo(
    () =>
      new SearchParameters({
        hitsPerPage: 32,
        filters,
      }),
    [filters]
  )

  return (
    <>
      <Seo
        title="The Expert - Shop Designer Curated Home Decor"
        description="Get the look of your favorite interior designer by shopping the Showrooms of Experts from Jake Arnold to Jenna Lyons. Discover vintage and new furniture, lighting, rugs, and more."
      />
      <InstantSearch
        key={routingKey}
        searchClient={searchClient}
        indexName="products-v5"
        routing
      >
        <ShopPageLayout
          searchParameters={searchParameters}
          showParentLevel={false}
          defaultSortIndex={1}
        />
      </InstantSearch>
    </>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const [baseProps, { data: newArrivalsPage }] = await Promise.all([
    prepareGetStaticNavigationProps(),
    Axios.client().getNewArrivalsPage({
      populate: [
        'includedBrands',
        'includedProductCategories',
        'includedProductCategories.parentProductCategory',
        'includedProductCategories.parentProductCategory.parentProductCategory',
      ],
    } as never),
  ])

  const includedBrands = (newArrivalsPage?.includedBrands ?? []).map(
    ({ name }) => name
  )
  const includedProductCategories = (
    newArrivalsPage?.includedProductCategories ?? []
  ).map((productCategory) => {
    let currentCategory = productCategory
    const categories = []

    while (currentCategory) {
      categories.unshift(currentCategory.name)
      currentCategory = currentCategory.parentProductCategory as
        | typeof currentCategory
        | null
    }

    return categories.join(' > ')
  })

  return {
    props: {
      ...baseProps.props,
      numberOfDays: newArrivalsPage?.numberOfDays ?? 60,
      includedBrands,
      includedProductCategories,
    },
    revalidate: 24 * 3600,
    notFound: baseProps.notFound,
  }
}

NewArrivalsPage.getLayout = (page) =>
  getPublicLayout(page, { isWithBreadcrumbs: true, isFullWidth: true })
